import { useEffect, useRef } from 'react';
import { renderBaseArtwork } from './heroArtwork';
import { FRAGMENT_SHADER, VERTEX_SHADER } from './heroShaders';

// Timing / geometry constants.
const DISC_MAX_RADIUS = 280;               // px in CSS space
const DISC_EASE_MS = 400;                  // enter / leave animation
const DOT_PITCH_RATIO = 0.0075;            // 0.75% of shorter edge
const CURSOR_FOLLOW_LAG_MS = 80;
const CHROMA_MAX = 12;                     // px, big enough to be visible
const CHROMA_EASE_MS = 150;
const MICRO_ZOOM_MAX = 1.02;
const MICRO_ZOOM_EASE_MS = 300;

function easeOutCubic(t: number): number {
  const x = 1 - t;
  return 1 - x * x * x;
}

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Failed to create shader');
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile error: ${log}`);
  }
  return shader;
}

function createProgram(gl: WebGLRenderingContext): WebGLProgram {
  const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
  const program = gl.createProgram();
  if (!program) throw new Error('Failed to create program');
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link error: ${log}`);
  }
  return program;
}

export default function HalftoneHero() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const gl = canvas.getContext('webgl', {
      premultipliedAlpha: false,
      antialias: false,
      preserveDrawingBuffer: false,
    }) as WebGLRenderingContext | null;

    if (!gl) return;

    const sourceCanvas = document.createElement('canvas');

    // ---------- WebGL setup ----------
    const program = createProgram(gl);
    gl.useProgram(program);

    const quad = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const sourceTexture = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, sourceTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    const uSource = gl.getUniformLocation(program, 'u_source');
    const uRes = gl.getUniformLocation(program, 'u_resolution');
    const uCursor = gl.getUniformLocation(program, 'u_cursor');
    const uDisc = gl.getUniformLocation(program, 'u_discRadius');
    const uChroma = gl.getUniformLocation(program, 'u_chroma');
    const uZoom = gl.getUniformLocation(program, 'u_zoom');
    const uHoverStrength = gl.getUniformLocation(program, 'u_hoverStrength');
    const uDotPitch = gl.getUniformLocation(program, 'u_dotPitch');
    gl.uniform1i(uSource, 0);

    // ---------- State ----------
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let cssWidth = 0;
    let cssHeight = 0;
    let pxWidth = 0;
    let pxHeight = 0;

    let cursorTargetX = 0;
    let cursorTargetY = 0;
    let cursorX = 0;
    let cursorY = 0;
    let cursorSpeed = 0;   // px/ms (smoothed)
    let lastMoveTs = 0;

    let discRadius = 0;
    let discAnimStart = 0;
    let discAnimFrom = 0;
    let discAnimTo = 0;
    let discTargetRadius = 0;

    let chroma = 0;
    let zoom = 1.0;

    let pointerActive = false;

    // ---------- Layout / source-texture rebuild ----------
    function rebuildArtwork() {
      const rect = container!.getBoundingClientRect();
      cssWidth = Math.max(rect.width, 320);
      cssHeight = Math.max(rect.height, 320);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      pxWidth = Math.round(cssWidth * dpr);
      pxHeight = Math.round(cssHeight * dpr);

      canvas!.style.width = `${cssWidth}px`;
      canvas!.style.height = `${cssHeight}px`;
      canvas!.width = pxWidth;
      canvas!.height = pxHeight;

      renderBaseArtwork(sourceCanvas, pxWidth, pxHeight);

      gl!.bindTexture(gl!.TEXTURE_2D, sourceTexture);
      gl!.texImage2D(
        gl!.TEXTURE_2D,
        0,
        gl!.RGBA,
        gl!.RGBA,
        gl!.UNSIGNED_BYTE,
        sourceCanvas,
      );
      gl!.viewport(0, 0, pxWidth, pxHeight);
    }

    rebuildArtwork();

    const resizeObserver = new ResizeObserver(() => {
      rebuildArtwork();
    });
    resizeObserver.observe(container);

    // ---------- Pointer / touch handling ----------
    function updatePointer(clientX: number, clientY: number, ts: number) {
      const rect = container!.getBoundingClientRect();
      const nx = clientX - rect.left;
      const ny = clientY - rect.top;
      const dt = Math.max(ts - lastMoveTs, 1);
      const dx = nx - cursorTargetX;
      const dy = ny - cursorTargetY;
      const instant = Math.sqrt(dx * dx + dy * dy) / dt;
      cursorSpeed = cursorSpeed * 0.7 + instant * 0.3;
      cursorTargetX = nx;
      cursorTargetY = ny;
      lastMoveTs = ts;
    }

    function beginDisc(ts: number) {
      pointerActive = true;
      discAnimFrom = discRadius;
      discAnimTo = DISC_MAX_RADIUS;
      discTargetRadius = DISC_MAX_RADIUS;
      discAnimStart = ts;
    }

    function endDisc(ts: number) {
      pointerActive = false;
      cursorSpeed = 0;
      discAnimFrom = discRadius;
      discAnimTo = 0;
      discTargetRadius = 0;
      discAnimStart = ts;
    }

    function onPointerEnter(e: PointerEvent) {
      const rect = container!.getBoundingClientRect();
      cursorTargetX = e.clientX - rect.left;
      cursorTargetY = e.clientY - rect.top;
      cursorX = cursorTargetX;
      cursorY = cursorTargetY;
      beginDisc(performance.now());
    }
    function onPointerMove(e: PointerEvent) {
      updatePointer(e.clientX, e.clientY, performance.now());
    }
    function onPointerLeave() {
      endDisc(performance.now());
    }

    function onTouchStart(e: TouchEvent) {
      const t = e.touches[0];
      if (!t) return;
      const rect = container!.getBoundingClientRect();
      cursorTargetX = t.clientX - rect.left;
      cursorTargetY = t.clientY - rect.top;
      cursorX = cursorTargetX;
      cursorY = cursorTargetY;
      beginDisc(performance.now());
    }
    function onTouchMove(e: TouchEvent) {
      const t = e.touches[0];
      if (!t) return;
      updatePointer(t.clientX, t.clientY, performance.now());
    }
    function onTouchEnd() {
      endDisc(performance.now());
    }

    container.addEventListener('pointerenter', onPointerEnter);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerleave', onPointerLeave);
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: true });
    container.addEventListener('touchend', onTouchEnd);
    container.addEventListener('touchcancel', onTouchEnd);

    // ---------- Render loop ----------
    let rafId = 0;
    let prevTs = 0;

    function tick(ts: number) {
      const dtMs = prevTs ? ts - prevTs : 16;
      prevTs = ts;

      // Cursor smoothing (80ms follow).
      const followAlpha = 1 - Math.exp(-dtMs / CURSOR_FOLLOW_LAG_MS);
      cursorX += (cursorTargetX - cursorX) * followAlpha;
      cursorY += (cursorTargetY - cursorY) * followAlpha;

      // Disc radius easing (ease-out cubic).
      if (discAnimStart) {
        const t = Math.min((ts - discAnimStart) / DISC_EASE_MS, 1);
        discRadius = discAnimFrom + (discAnimTo - discAnimFrom) * easeOutCubic(t);
        if (t >= 1) discRadius = discAnimTo;
      }

      // Chromatic aberration follows pointer speed (only when active).
      const speedTarget = pointerActive
        ? Math.min(cursorSpeed / 1.8, 1) * CHROMA_MAX
        : 0;
      if (pointerActive && ts - lastMoveTs > 40) {
        cursorSpeed *= Math.pow(0.001, dtMs / 300);
      }
      const chromaAlpha = 1 - Math.exp(-dtMs / CHROMA_EASE_MS);
      chroma += (speedTarget - chroma) * chromaAlpha;

      // Micro-zoom easing (1.00 ↔ 1.02).
      const zoomTarget = discTargetRadius > 0 ? MICRO_ZOOM_MAX : 1.0;
      const zoomAlpha = 1 - Math.exp(-dtMs / MICRO_ZOOM_EASE_MS);
      zoom += (zoomTarget - zoom) * zoomAlpha;

      // Push uniforms & draw.
      gl!.useProgram(program);
      gl!.viewport(0, 0, pxWidth, pxHeight);

      gl!.uniform2f(uRes, pxWidth, pxHeight);
      gl!.uniform2f(uCursor, cursorX * dpr, cursorY * dpr);
      gl!.uniform1f(uDisc, discRadius * dpr);
      gl!.uniform1f(uChroma, chroma * dpr);
      gl!.uniform1f(uZoom, zoom);
      // Hover strength: 0 at rest, eases to 1 when the disc is fully open.
      gl!.uniform1f(uHoverStrength, Math.min(discRadius / DISC_MAX_RADIUS, 1));
      gl!.uniform1f(uDotPitch, Math.max(3, Math.min(pxWidth, pxHeight) * DOT_PITCH_RATIO));

      gl!.drawArrays(gl!.TRIANGLE_STRIP, 0, 4);

      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      container.removeEventListener('pointerenter', onPointerEnter);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerleave', onPointerLeave);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);

      gl.deleteBuffer(buffer);
      gl.deleteTexture(sourceTexture);
      gl.deleteProgram(program);
    };
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative w-full h-screen min-h-[720px] overflow-hidden bg-white select-none touch-none cursor-crosshair"
      aria-label="CMYK halftone hero"
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full block"
      />

      {/* Typography layer — separate from the WebGL canvas, stays crisp */}
      <div className="pointer-events-none absolute inset-0 z-10">
        {/* Top-left meta */}
        <div className="absolute top-6 left-6 md:top-10 md:left-12 text-[#241A5E]">
          <div className="text-[10px] md:text-[11px] tracking-[0.4em] font-bold uppercase">
            Issue Nº 07 · MMXXVI
          </div>
          <div className="mt-1 text-[10px] md:text-[11px] tracking-[0.4em] font-bold uppercase opacity-70">
            CMYK Glitch · Flora Study
          </div>
        </div>

        {/* Top-right meta */}
        <div className="absolute top-6 right-6 md:top-10 md:right-12 text-right text-[#241A5E]">
          <div
            className="text-4xl md:text-6xl leading-none"
            style={{ fontFamily: '"Archivo Black", sans-serif' }}
          >
            N°07
          </div>
          <div className="mt-2 text-[10px] md:text-[11px] tracking-[0.35em] font-bold uppercase opacity-70">
            15° / 75° / 0° / 45°
          </div>
        </div>

        {/* Main title — sits in the upper zone, above the flower strip */}
        <div className="absolute left-6 md:left-12 top-[18%] md:top-[16%] text-[#241A5E]">
          <h1
            className="leading-[0.85] tracking-tight"
            style={{
              fontFamily: '"Archivo Black", "Inter", sans-serif',
              fontSize: 'clamp(48px, 8vw, 132px)',
              letterSpacing: '-0.02em',
            }}
          >
            <span className="block">STATIC</span>
            <span className="block">GARDEN</span>
          </h1>
          <p
            className="mt-4 md:mt-6 max-w-md text-sm md:text-base font-medium leading-relaxed"
            style={{ fontFamily: '"Inter", sans-serif' }}
          >
            A hand-tuned WebGL press: four ink plates, one botanical stage,
            and a cursor that pulls them apart.
          </p>
        </div>

        {/* Bottom-left tag */}
        <div className="absolute bottom-6 left-6 md:bottom-10 md:left-12 text-[10px] md:text-[11px] tracking-[0.4em] font-bold uppercase text-[#241A5E]">
          — Drift · Bloom · Noise —
        </div>
        {/* Bottom-right tag */}
        <div className="absolute bottom-6 right-6 md:bottom-10 md:right-12 text-[10px] md:text-[11px] tracking-[0.4em] font-bold uppercase text-[#241A5E]">
          Webgl / Halftone / R:280
        </div>

        {/* Center hint */}
        <div className="absolute inset-x-0 bottom-16 md:bottom-24 flex items-center justify-center">
          <span className="text-[10px] tracking-[0.5em] text-[#241A5E]/60 font-semibold uppercase">
            Hover to break the plates apart
          </span>
        </div>
      </div>
    </section>
  );
}